import React, { useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { VoiceNotePlayer } from './VoiceNotePlayer';
import { supabase } from '@/integrations/supabase/client';
import { GameState, getAllLegalMoves, validateMove } from '@/lib/chess-engine';
import { cn } from '@/lib/utils';
import { MessageSquare } from 'lucide-react';
import { gameStateToFen } from '@/lib/fen-parser';
import { uciToSan } from '@/lib/move-notation';

interface VoiceNote {
  id: string;
  move_number: number;
  player_id: string;
  audio_file_path: string;
  duration_ms: number;
  transcription?: string;
  created_at: string;
}

interface MoveHistoryProps {
  gameStates: GameState[];
  currentMoveIndex: number;
  onMoveClick: (moveIndex: number) => void;
  gameSessionId?: string;
}

export function MoveHistory({ gameStates, currentMoveIndex, onMoveClick, gameSessionId }: MoveHistoryProps) {
  const [voiceNotes, setVoiceNotes] = useState<VoiceNote[]>([]);

  // Load voice notes for the game session
  useEffect(() => {
    if (!gameSessionId) return;

    const loadVoiceNotes = async () => {
      const { data, error } = await supabase
        .from('move_voice_notes')
        .select('*')
        .eq('game_session_id', gameSessionId)
        .order('move_number', { ascending: true });

      if (error) {
        console.error('Error loading voice notes:', error);
      } else {
        setVoiceNotes(data || []);
      }
    };

    loadVoiceNotes();
  }, [gameSessionId]);

  const getVoiceNoteForMove = (moveNumber: number) => {
    return voiceNotes.find(note => note.move_number === moveNumber);
  };

  const formatMoveNumber = (index: number) => {
    if (index === 0) return '';
    const moveNumber = Math.ceil(index / 2);
    const isWhiteMove = index % 2 === 1;
    return isWhiteMove ? `${moveNumber}.` : '';
  };

  const formatMove = (gameState: GameState, index: number, gameStates: GameState[]) => {
    if (index === 0) return 'Start';
    
    // Get the move that led to this position
    const previousState = gameStates[index - 1];
    if (!previousState) return `Move ${index}`;
    
    // Find what move was made by comparing board states
    const move = findMoveBetweenStates(previousState, gameState);
    if (!move) return `Move ${index}`;
    
    // Convert to algebraic notation
    try {
      return uciToSan(previousState, move);
    } catch {
      return move; // Fallback to UCI notation
    }
  };

  // Robust function to find the move between two game states using legal moves
  const findMoveBetweenStates = (fromState: GameState, toState: GameState): string | null => {
    try {
      // Get all legal moves from the previous state
      const legalMoves = getAllLegalMoves(fromState);
      
      // Test each legal move to see if it produces the target state
      for (const move of legalMoves) {
        try {
          const moveResult = validateMove(fromState, move.from, move.to, move.promotion);
          if (moveResult.isLegal && moveResult.newState) {
            // Compare the resulting FEN with the target state FEN
            const resultFen = gameStateToFen(moveResult.newState);
            const targetFen = gameStateToFen(toState);
            
            if (resultFen === targetFen) {
              // Found the matching move - convert to UCI notation
              const fromSquare = String.fromCharCode(97 + move.from[1]) + (8 - move.from[0]);
              const toSquare = String.fromCharCode(97 + move.to[1]) + (8 - move.to[0]);
              let uciMove = fromSquare + toSquare;
              
              // Add promotion if present
              if (move.promotion) {
                uciMove += move.promotion.toLowerCase();
              }
              
              return uciMove;
            }
          }
        } catch (error) {
          // Skip invalid moves
          continue;
        }
      }
      
      return null;
    } catch (error) {
      console.warn('Error finding move between states:', error);
      return null;
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b">
        <h3 className="font-semibold text-sm">Move History</h3>
      </div>
      
      <ScrollArea className="flex-1 p-2">
        <div className="space-y-1">
          {gameStates.map((gameState, index) => {
            const voiceNote = getVoiceNoteForMove(index);
            return (
              <div key={index} className="flex items-center space-x-2">
                <Button
                  variant={index === currentMoveIndex ? "secondary" : "ghost"}
                  size="sm"
                  className={cn(
                    "flex-1 justify-start text-xs h-8",
                    index === currentMoveIndex && "bg-primary/10"
                  )}
                  onClick={() => onMoveClick(index)}
                >
                  <span className="font-mono">
                    {formatMoveNumber(index)} {formatMove(gameState, index, gameStates)}
                  </span>
                </Button>
                
                {voiceNote && (
                  <VoiceNotePlayer
                    voiceNote={voiceNote}
                    compact={true}
                    className="flex-shrink-0"
                  />
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}